import { useEffect, useMemo, useRef } from "react";
import Swal from "sweetalert2";
import {
    DESIGNER_ACTIONS,
    buildDefaultSelectedKeys,
    getWizardPartNodes,
} from "../../utils/designerState";
import {
    getDesignerDraftKey,
    parseDesignerDraft,
    serializeDesignerDraft,
} from "../../utils/designerDraft";

const useDesignerDraftLifecycle = ({
    isDesignerOpen,
    isPageVariant,
    normalizedRoot,
    treeContext,
    orderItemId,
    bSide,
    productModelId,
    dispatch,
    state,
    currentStep,
    totalSteps,
    setCurrentStep,
    setSaveMsg,
    setSketchError,
    setSketchImage,
    setExpandedPalettes,
    setPrintHotspotFilter,
    setShowPrintHotspots,
    setSideConfirm,
    setStripeDrawingsBySide,
    stripeDrawingsBySide,
    setCustomCutsBySide,
    customCutsBySide,
    setCustomCutZoneColors,
    customCutZoneColors,
    setCustomCutEditor,
    customCutEditor,
    activePartSectionKey,
    setActivePartSectionKey,
}) => {
    const draftReadyRef = useRef(false);
    const draftSaveAlertShownRef = useRef(false);

    const draftStorageKey = useMemo(
        () => getDesignerDraftKey({ orderItemId, bSide, productModelId }),
        [orderItemId, bSide, productModelId]
    );

    useEffect(() => {
        if (!isDesignerOpen || !normalizedRoot) return;
        let isCancelled = false;
        const initDesignerState = async () => {
            const defaults = buildDefaultSelectedKeys(normalizedRoot, treeContext);
            const knownPartKeys = Object.keys(treeContext.nodeByKey || {});
            const wizardParts = getWizardPartNodes({
                root: normalizedRoot,
                context: treeContext,
                selectedKeys: defaults,
            });
            const firstPart = wizardParts.find((part) => defaults.has(part.key))?.key || wizardParts[0]?.key || null;
            draftReadyRef.current = false;
            let restored = false;

            if (typeof window !== "undefined" && orderItemId) {
                const raw = window.localStorage.getItem(draftStorageKey);
                const parsed = parseDesignerDraft({
                    raw,
                    orderItemId,
                    bSide,
                    knownPartKeys,
                });
                if (parsed.ok && parsed.draft?.state) {
                    const result = await Swal.fire({
                        icon: "question",
                        title: "Borrador encontrado",
                        text: "Encontramos un borrador local del diseño. ¿Querés continuarlo?",
                        showCancelButton: true,
                        showCloseButton: true,
                        confirmButtonText: "Sí, continuar",
                        cancelButtonText: "No, empezar nuevo",
                        confirmButtonColor: "#0071ce",
                        cancelButtonColor: "#d54ab7",
                    });
                    if (isCancelled) return;
                    if (result.isConfirmed) {
                        let restoredStripes = parsed.draft?.stripeDrawingsBySide || { front: [], back: [] };
                        if (!parsed.draft?.hasStripeDrawings) {
                            try {
                                const legacyRaw = window.localStorage.getItem(`${draftStorageKey}:stripes`);
                                if (legacyRaw) {
                                    const legacyParsed = JSON.parse(legacyRaw);
                                    restoredStripes = {
                                        front: Array.isArray(legacyParsed?.front) ? legacyParsed.front : [],
                                        back: Array.isArray(legacyParsed?.back) ? legacyParsed.back : [],
                                    };
                                }
                            } catch (_legacyError) {
                            }
                        }
                        dispatch({
                            type: DESIGNER_ACTIONS.RESTORE_DRAFT,
                            draft: parsed.draft.state,
                        });
                        setStripeDrawingsBySide(restoredStripes);
                        setCustomCutsBySide(parsed.draft?.customCutsBySide || { front: [], back: [] });
                        setCustomCutZoneColors(parsed.draft?.customCutZoneColors || {});
                        setCustomCutEditor((prev) => ({
                            ...(prev || {}),
                            ...(parsed.draft?.customCutEditor || {}),
                        }));
                        setCurrentStep(Math.min(Math.max(Number(parsed.draft.currentStep || 1), 1), totalSteps));
                        setActivePartSectionKey(String(parsed.draft.activePartSectionKey || ""));
                        restored = true;
                    } else {
                        window.localStorage.removeItem(draftStorageKey);
                        window.localStorage.removeItem(`${draftStorageKey}:stripes`);
                    }
                }
            }

            if (isCancelled) return;
            if (!restored) {
                dispatch({
                    type: DESIGNER_ACTIONS.INIT,
                    selectedKeys: defaults,
                    activePartKey: firstPart,
                    viewSide: "front",
                });
                setCurrentStep(1);
                setActivePartSectionKey("");
                setStripeDrawingsBySide({ front: [], back: [] });
                setCustomCutsBySide({ front: [], back: [] });
                setCustomCutZoneColors({});
                setCustomCutEditor((prev) => ({
                    enabled: false,
                    tool: "none",
                }));
            }
            setSaveMsg(null);
            setSketchError(null);
            setSketchImage(null);
            setExpandedPalettes({});
            setPrintHotspotFilter(null);
            setShowPrintHotspots(false);
            draftReadyRef.current = true;
        };

        initDesignerState();
        return () => {
            isCancelled = true;
        };
    }, [
        isDesignerOpen,
        normalizedRoot,
        treeContext,
        orderItemId,
        bSide,
        draftStorageKey,
        dispatch,
        setCurrentStep,
        totalSteps,
        setSaveMsg,
        setSketchError,
        setSketchImage,
        setExpandedPalettes,
        setPrintHotspotFilter,
        setShowPrintHotspots,
        setStripeDrawingsBySide,
        setCustomCutsBySide,
        setCustomCutZoneColors,
        setCustomCutEditor,
        setActivePartSectionKey,
    ]);

    useEffect(() => {
        if (isPageVariant) return;
        if (isDesignerOpen) return;
        dispatch({ type: DESIGNER_ACTIONS.RESET });
        setCurrentStep(1);
        setSaveMsg(null);
        setSketchError(null);
        setSketchImage(null);
        setSideConfirm(null);
        setPrintHotspotFilter(null);
        setShowPrintHotspots(false);
        setStripeDrawingsBySide({ front: [], back: [] });
        setCustomCutsBySide({ front: [], back: [] });
        setCustomCutZoneColors({});
        setCustomCutEditor((prev) => ({
            enabled: false,
            tool: "none",
        }));
        setActivePartSectionKey("");
        draftReadyRef.current = false;
    }, [
        isDesignerOpen,
        isPageVariant,
        dispatch,
        setCurrentStep,
        setSaveMsg,
        setSketchError,
        setSketchImage,
        setSideConfirm,
        setPrintHotspotFilter,
        setShowPrintHotspots,
        setStripeDrawingsBySide,
        setCustomCutsBySide,
        setCustomCutZoneColors,
        setCustomCutEditor,
        setActivePartSectionKey,
    ]);

    useEffect(() => {
        if (!isDesignerOpen || !draftReadyRef.current || !orderItemId) return;
        const timer = setTimeout(() => {
            try {
                const payload = serializeDesignerDraft({
                    orderItemId,
                    bSide,
                    state,
                    currentStep,
                    stripeDrawingsBySide,
                    customCutsBySide,
                    customCutZoneColors,
                    customCutEditor,
                    activePartSectionKey,
                });
                if (!payload || typeof window === "undefined") return;
                window.localStorage.setItem(draftStorageKey, JSON.stringify(payload));
            } catch (error) {
                if (!draftSaveAlertShownRef.current) {
                    draftSaveAlertShownRef.current = true;
                    Swal.fire({
                        icon: "warning",
                        title: "No pudimos guardar el borrador",
                        text: "Hubo un problema guardando el borrador local. Podés seguir diseñando.",
                        showCloseButton: true,
                        confirmButtonColor: "#0071ce",
                    });
                }
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [isDesignerOpen, orderItemId, bSide, state, currentStep, draftStorageKey, stripeDrawingsBySide, customCutsBySide, customCutZoneColors, customCutEditor, activePartSectionKey]);

    return {
        draftStorageKey,
    };
};

export default useDesignerDraftLifecycle;
